import { Close } from '@mui/icons-material';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
  closeIcon,
  dialogTitle,
  iconButton,
  modal
} from 'styles/components/ModalStyle';

const MAX_REJECTION_REASON_LENGTH = 1000;

interface RejectDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (formData: any) => void;
  translate: (key: string) => string;
}

interface RejectDialogFormValues {
  rejectionReason: string;
}

export const RejectDialog = ({
  isOpen,
  onClose,
  onConfirm,
  translate
}: RejectDialogProps) => {
  const { control, handleSubmit, formState, reset, trigger } =
    useForm<RejectDialogFormValues>({
      mode: 'onBlur',
      defaultValues: {
        rejectionReason: ''
      }
    });

  const validateRejectionReason = (value: string) => {
    if (!value) {
      return translate('errors.rejectionReasonRequired');
    }

    if (value.length > MAX_REJECTION_REASON_LENGTH) {
      return translate('errors.rejectionReasonMaxLength');
    }

    // Check if value contains at least one Unicode letter
    const hasLetter = /.*\p{L}.*/u.test(value);
    if (!hasLetter) {
      return translate('errors.rejectionReasonMustContainLetter');
    }

    return undefined;
  };

  useEffect(() => {
    if (isOpen) {
      reset({ rejectionReason: '' });
      // Trigger validation immediately so errors are shown and button is disabled
      trigger('rejectionReason');
    }
  }, [isOpen, reset, trigger]);

  const onSubmit = (data: RejectDialogFormValues) => {
    onConfirm(data);
  };

  const isFormInvalid = () => {
    return (
      !!formState.errors.rejectionReason ||
      !formState.dirtyFields.rejectionReason
    );
  };

  return (
    <Dialog open={isOpen} sx={modal} maxWidth="sm">
      <DialogTitle sx={dialogTitle}>
        {translate('titles.rejectExpense')}
        <IconButton onClick={onClose} sx={iconButton}>
          <Close sx={closeIcon} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ mt: '1.5rem' }}>
        <Box sx={{ width: '100%', mb: 2 }}>
          <Controller
            name="rejectionReason"
            control={control}
            rules={{
              validate: validateRejectionReason
            }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                multiline
                rows={4}
                fullWidth
                label={`${translate('labels.rejectionReason')}*`}
                placeholder={translate('labels.rejectionReason')}
                error={!!error}
                helperText={error ? error.message : ''}
                sx={{
                  mt: 2,
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused': {
                      '& fieldset': {
                        borderColor: error ? '#EF4444' : undefined
                      }
                    }
                  }
                }}
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          {translate('buttons.cancel')}
        </Button>
        <Button
          disabled={isFormInvalid()}
          onClick={handleSubmit(onSubmit)}
          variant="contained"
        >
          {translate('buttons.reject')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
